import { deleteNotification } from '@/src/actions/notifications';
import { AvatarOrFallback } from './MiniProfileInfo';
import { IUINotification } from '@/src/types/interfaces';
import { getNotificationData } from '@/src/utils/notificationUtils';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@workspace/ui/components/dropdown-menu';
import moment from 'moment';
import { useRouter } from 'next/navigation';
import { useBoolean } from '@/src/hooks/use-boolean';
import { FullscreenLoader } from './FullscreenLoader';
import { ENotificationType } from 'types/src/types';
import { cn } from '@workspace/ui/lib/utils';

type Props = { notification: IUINotification; onRead?: () => void };

export const Notification = ({ notification, onRead }: Props) => {
  const { link, text } = getNotificationData(notification);
  const router = useRouter();
  const isLoading = useBoolean(false);

  const handleClick = async () => {
    isLoading.onTrue();
    try {
      await deleteNotification(notification.id as string);
      onRead?.();
    } finally {
      router.push(link);
      isLoading.onFalse();
    }
  };

  const logoImg =
    notification.type === ENotificationType.PAYOUT ||
    notification.type === ENotificationType.REJECTED_PROJECT ||
    notification.type === ENotificationType.APPROVED_PROJECT ||
    notification.type === ENotificationType.ERROR_PROJECT;

  return (
    <>
      {isLoading.value && <FullscreenLoader />}
      <DropdownMenuItem
        key={notification.id}
        className="flex items-center justify-between md:gap-3 md:py-2 md:p-4 p-2 bg-bg-gray rounded-none cursor-pointer hover:underline transition-all duration-200 ease-in-out"
        onClick={handleClick}
      >
        <div className="flex md:gap-3 gap-1 items-center">
          <AvatarOrFallback
            fullName={notification.producer?.name as string}
            authorImage={
              logoImg ? '/icons/logo.svg' : notification.producer?.avatarUrl
            }
            className="size-12"
          />
          <div className="flex flex-col md:text-sm text-xs font-light justify-between md:w-[287px] w-50 md:gap-3 gap-1 pr-2">
            <div>
              {text.map(({ type, value, isRed }, idx) => (
                <span
                  key={idx}
                  className={cn(
                    'md:text-sm text-xs font-normal text-black/60 break-words ',
                    { 'line-clamp-3': isRed },
                  )}
                >
                  {type === 'bold' ? (
                    <span
                      className={cn(
                        'text-black font-medium md:text-sm text-xs',
                        { 'text-red-error': isRed },
                      )}
                    >
                      {value}
                    </span>
                  ) : (
                    value
                  )}
                </span>
              ))}
            </div>
            {moment(notification.createdAt).fromNow()}
          </div>
        </div>

        <div className="rounded-full size-2 bg-blue-primary" />
      </DropdownMenuItem>
      <DropdownMenuSeparator className="m-0" />
    </>
  );
};
